import type { ComboBoxProps } from "@react-types/combobox";
import type { LoadingState } from "@react-types/shared";
import { XIcon } from "lucide-react";
import React, { ReactNode, useRef } from "react";
import { useButton, useComboBox, useFilter, useSearchField } from "react-aria";
import { SearchFieldProps, useComboBoxState, useSearchFieldState } from "react-stately";
import { Prettify } from "../../../utils/types";
import Flex from "../../Flex";
import { Typography } from "../../Typography";
import { Label } from "../Field/styled";
import InputLike from "../InputLike";
import { ListBox } from "./ListBox";
import { Popover } from "../../Popover";
import { Spinner } from "./Spinner";
import * as S from "./styled";

export type AutocompleteProps<T> = Prettify<
    {
        /** An icon for the input field. Most likely, you want to use a magnifier glass here. */
        icon?: ReactNode;
        /**
         * The loading state of the items.
         *
         * `loading` and `filtering` show a spinner inside the input field.
         * `loadingMore` shows a spinner inside the list box.
         *
         * Suggestion: use `useAsyncList` from `react-stately`, and the loading state will be handled for you for free.
         * */
        loadingState?: LoadingState;
        /** The name of the form field. Useful for uncontrolled forms. */
        name?: string;
        /** A callback for loading more items into the collection. */
        onLoadMore?: () => void;
        /**
         * When the Autocomplete is mounted inside an element that scrolls, use this prop to pass a reference of the
         * scrolling element. This allows the Autocomplete to reposition its popover, if visible, when the content moves.
         */
        scrollingContainer?: Element;
    } & ComboBoxProps<T>
>;

const Autocomplete = <T extends object>({ icon, scrollingContainer, ...props }: AutocompleteProps<T>) => {
    const { contains } = useFilter({ sensitivity: "base" });
    const state = useComboBoxState({ ...props, defaultFilter: contains });
    const inputRef = useRef(null);
    const listBoxRef = useRef(null);
    const popoverRef = useRef(null);
    const triggerRef = useRef(null);
    const { inputProps, listBoxProps, labelProps } = useComboBox(
        {
            ...props,
            inputRef,
            listBoxRef,
            popoverRef
        },
        state
    );

    const searchProps: SearchFieldProps = {
        label: props.label,
        value: state.inputValue,
        onChange: (v: string) => state.setInputValue(v)
    };

    const searchState = useSearchFieldState(searchProps);
    const { clearButtonProps } = useSearchField(searchProps, searchState, inputRef);
    const clearButtonRef = useRef(null);
    const { buttonProps } = useButton(clearButtonProps, clearButtonRef);

    const isLoading = props.loadingState === "loading" || props.loadingState === "filtering";

    return (
        <Flex column gap={8}>
            <Label {...labelProps}>{props.label}</Label>

            <InputLike
                ref={triggerRef}
                clear={isLoading ? <Spinner size={20} /> : null}
                disabled={props.isDisabled}
                error={!!props.errorMessage}
                icon={icon}
                trail={
                    props.isDisabled ? undefined : (
                        <S.ClearButton
                            type="button"
                            {...buttonProps}
                            ref={clearButtonRef}
                            style={{ visibility: state.inputValue !== "" ? "visible" : "hidden" }}
                        >
                            <XIcon aria-hidden="true" size={20} />
                        </S.ClearButton>
                    )
                }
            >
                <input {...inputProps} ref={inputRef} />
            </InputLike>

            {(props.errorMessage || props.description) && (
                <Typography color={props.errorMessage ? "error-500" : "primary-700"} variant="label-x-small-regular">
                    {props.errorMessage || props.description}
                </Typography>
            )}

            {state.isOpen && (
                <Popover
                    isNonModal
                    offset={8}
                    useTriggerWidth
                    placement="bottom start"
                    popoverRef={popoverRef}
                    scrollingContainer={scrollingContainer}
                    state={state}
                    triggerRef={triggerRef}
                >
                    <ListBox
                        {...listBoxProps}
                        listBoxRef={listBoxRef}
                        loadingState={props.loadingState}
                        onLoadMore={props.onLoadMore}
                        state={state}
                    />
                </Popover>
            )}
        </Flex>
    );
};

export default Autocomplete;
